import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Legend, LineChart, Line,
} from "recharts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, BarChart3, PieChart as PieIcon, LineChart as LineIcon } from "lucide-react";

const PIE_COLORS = ["#2563EB", "#16A34A", "#CA8A04", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#EA580C", "#65A30D", "#0F766E"];
const ACTIVITY_LABELS = {
  MISSION: "Missions",
  PAID_LEAVE: "Paid leave",
  SICK_LEAVE: "Sick leave",
  TRAINING: "Training",
  ONBOARDING: "Onboarding",
};

function firstOfYear() { const y = new Date().getFullYear(); return `${y}-01-01`; }
function today() { return new Date().toISOString().slice(0, 10); }

export default function Stats() {
  const [start, setStart] = useState(firstOfYear());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!start || !end) return;
    setLoading(true);
    try {
      const r = await api.get(`/stats/me?start=${start}&end=${end}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    if (preset === "month") { setStart(`${y}-${String(m + 1).padStart(2, "0")}-01`); setEnd(today()); }
    else if (preset === "ytd") { setStart(`${y}-01-01`); setEnd(today()); }
    else if (preset === "last30") {
      const d = new Date(); d.setDate(d.getDate() - 30);
      setStart(d.toISOString().slice(0, 10)); setEnd(today());
    }
    else if (preset === "lastquarter") {
      const d = new Date(); d.setMonth(d.getMonth() - 3);
      setStart(d.toISOString().slice(0, 10)); setEnd(today());
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="stats-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Your activity</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Stats</h1>
        <p className="text-zinc-500 mt-3 max-w-2xl">Breakdown of your own work and absences over a custom period — by activity type, mission, and monthly trend.</p>
      </div>

      {/* Period picker */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-8">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2 block">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="stats-start" className="w-40" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2 block">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="stats-end" className="w-40" />
          </div>
          <button onClick={load} disabled={loading} data-testid="stats-apply" className="px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
            {loading ? "Loading…" : "Apply"}
          </button>
          <div className="flex gap-2 ml-auto">
            {[
              { k: "month", l: "This month" },
              { k: "last30", l: "Last 30d" },
              { k: "lastquarter", l: "Last 3 months" },
              { k: "ytd", l: "Year to date" },
            ].map((p) => (
              <button key={p.k} onClick={() => { applyPreset(p.k); setTimeout(load, 100); }} data-testid={`stats-preset-${p.k}`} className="text-xs px-3 h-9 border border-zinc-200 rounded-md hover:bg-zinc-50">{p.l}</button>
            ))}
          </div>
        </div>
      </div>

      {!data && <div className="text-zinc-500 text-sm">No data yet.</div>}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <KPI label="Total hours" value={data.total_hours} sub={`${data.total_jh} JH`} />
            <KPI label="Missions worked" value={data.totals_by_mission.length} />
            <KPI label="Activity types" value={data.totals_by_activity.length} />
            <KPI label="Period" value={`${data.start} → ${data.end}`} small />
          </div>

          {/* Activity + Mission breakdown side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <Panel title="By activity type" icon={<PieIcon size={14} />}>
              {data.totals_by_activity.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={data.totals_by_activity.map(a => ({ name: ACTIVITY_LABELS[a.activity_type] || a.activity_type, value: a.hours }))}
                         cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name} (${e.value}h)`} dataKey="value">
                      {data.totals_by_activity.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v} h`, "Hours"]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Panel>
            <Panel title="By mission" icon={<BarChart3 size={14} />}>
              {data.totals_by_mission.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.totals_by_mission.slice(0, 8).map(m => ({ name: m.title.slice(0, 20), JH: m.jh, color: m.color || "#18181B" }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="JH" fill="#2563EB">
                      {data.totals_by_mission.slice(0, 8).map((m, i) => <Cell key={i} fill={m.color || "#2563EB"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </div>

          {/* Monthly trend */}
          <Panel title="Monthly trend" icon={<LineIcon size={14} />}>
            {data.monthly_trend.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, n) => [`${v} ${n === "hours" ? "h" : "JH"}`, n]} />
                  <Legend />
                  <Line type="monotone" dataKey="hours" stroke="#2563EB" strokeWidth={2} name="Hours" />
                  <Line type="monotone" dataKey="jh" stroke="#16A34A" strokeWidth={2} name="JH" strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* Raw missions table */}
          <div className="mt-10">
            <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><CalendarDays size={16} /> Missions detail</h3>
            <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto md:overflow-hidden">
              <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
                <div className="col-span-6">Mission</div>
                <div className="col-span-3">Client</div>
                <div className="col-span-2 text-right">Hours</div>
                <div className="col-span-1 text-right">JH</div>
              </div>
              {data.totals_by_mission.map((m) => (
                <div key={m.mission_id} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50">
                  <div className="col-span-6 flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full" style={{ background: m.color || "#71717A" }} />
                    <div className="truncate text-sm">{m.title}</div>
                  </div>
                  <div className="col-span-3 text-xs text-zinc-600 truncate">{m.client_name}</div>
                  <div className="col-span-2 text-right text-sm font-mono">{m.hours}</div>
                  <div className="col-span-1 text-right text-sm font-mono font-medium">{m.jh}</div>
                </div>
              ))}
              {data.totals_by_mission.length === 0 && <div className="px-5 py-12 text-sm text-zinc-500 text-center">No mission hours in this period.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, sub, small }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">{label}</div>
      <div className={small ? "text-sm font-mono text-zinc-900" : "font-heading text-3xl font-bold tracking-tight"}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 font-mono mt-1">{sub}</div>}
    </div>
  );
}

function Panel({ title, icon, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4 text-zinc-900">
        {icon}
        <h3 className="font-heading font-semibold text-sm uppercase tracking-wider">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Empty() {
  return <div className="text-zinc-400 text-sm text-center py-12">No data</div>;
}
